import React, { useState, Children } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ChefHat, Package, BarChart3, LogOut, Menu, X, Store, UtensilsCrossed, Armchair, Pizza, GlassWater, Gift, Award, ClipboardList, FileText, ChevronDown, Wrench, Building2, QrCode, Siren } from 'lucide-react';
import { Restaurant, TenantRole } from '../../lib/types';
import { cn } from '../../lib/utils';
import { useInvoiceRequests } from '../../lib/invoice-requests-hooks';
interface AdminLayoutProps {
  restaurant: Restaurant | null;
  role?: TenantRole;
  onSignOut?: () => void;
  children: React.ReactNode;
}
interface NavItem {
  name: string;
  path: string;
  icon: any;
  roles: TenantRole[];
  badge?: number;
}
interface NavGroup {
  type: 'group';
  name: string;
  icon: any;
  roles: TenantRole[];
  // Children can be flat NavItems OR nested NavGroups (one level deep).
  // We rely on the runtime `isGroup()` check to discriminate.
  items: NavEntry[];
  defaultOpen?: boolean;
}
type NavEntry = NavItem | NavGroup;
const isGroup = (entry: NavEntry): entry is NavGroup => (entry as NavGroup).type === 'group';
export function AdminLayout({
  restaurant,
  role = 'admin',
  onSignOut,
  children
}: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    pendingRequests
  } = useInvoiceRequests(restaurant?.id);
  const pendingInvoiceCount = pendingRequests.length;
  // Collapsible group open state — persists per session
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    Estaciones: false,
    'Otras Herramientas': false,
    '911': false
  });
  if (!restaurant) return null;
  const slug = restaurant.slug;
  // La Cabaña Parrillera (id 24) — only Cocina + Batidos y postres in Estaciones.
  const isCabanaParrillera = String(restaurant.id) === '24' || restaurant.slug === 'la-cabana-parrillera' || restaurant.slug === 'cabana-parrillera';
  // Rancho San Francisco (id 28) — same nav shape as La Cabaña for the
  // Estaciones group: hide the Pizzería entry. Bebidas keeps its
  // default label ("Postres y Batidos").
  const isRanchoSanFrancisco = String(restaurant.id) === '28' || restaurant.slug === 'rancho-san-francisco';
  // NatuCafé Coffee Shop (id 19) — Tomar Orden y Control de Caja se
  // mueven dentro de "Otras Herramientas", agrupados bajo un sub-grupo
  // colapsable llamado "911" (acceso rápido durante el rush). El resto
  // del sidebar permanece igual: Mesas / Estaciones / Dashboard / Menú /
  // Otras Herramientas. El resto de restaurantes NO ven este cambio.
  const isNatuCafe = String(restaurant.id) === '19' || restaurant.slug === 'natucafe-coffee-shop';
  // Emilius Pizza (id 27) — usa la misma forma de sidebar que NatuCafé
  // (Tomar Orden y Control de Caja dentro del sub-grupo "911" en
  // "Otras Herramientas"), PERO mantiene todas sus estaciones activas
  // (Cocina / Pizzería / Postres y Batidos). NatuCafé NO se ve afectado.
  const isEmilius = String(restaurant.id) === '27' || restaurant.slug === 'emilius-pizza';
  // Build the nav structure (in display order)
  const allEntries: NavEntry[] = [
  // Rancho San Francisco — Tomar Orden vive dentro de "Otras Herramientas"
  // para simplificar el sidebar. NatuCafé — vive dentro del sub-grupo
  // "911" (también dentro de Otras Herramientas). Resto: nivel superior.
  ...(isRanchoSanFrancisco || isNatuCafe || isEmilius ? [] : [{
    name: 'Tomar Orden',
    path: `/admin/${slug}/waiter`,
    icon: ClipboardList,
    roles: ['owner', 'admin', 'manager', 'caja', 'waiter'] as TenantRole[]
  } as NavItem]), {
    name: 'Mesas',
    path: `/admin/${slug}/tables`,
    icon: Armchair,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  },
  // NatuCafé — Estantería se promueve al nivel superior, justo debajo
  // de Mesas, para que cajeros/dueños la encuentren al toque sin
  // expandir "Otras Herramientas". Sólo NatuCafé (id 19); para el
  // resto de restaurantes este item ni aparece.
  ...(isNatuCafe ? [{
    name: 'Estantería',
    path: `/admin/${slug}/pos-caja`,
    icon: Store,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  } as NavItem] as NavItem[] : []),
  // Rancho San Francisco — Control de Caja accesible desde el nivel
  // superior (debajo de Mesas) porque "Otras Herramientas" está oculto
  // para este restaurante. Dashboard se quita del sidebar — el dueño
  // quiere ver los pedidos abiertos directo en Control de Caja
  // bucketeados por Mesas / Para llevar / Express, donde además puede
  // cobrar y cerrar pedidos. Cero impacto en cualquier otro
  // restaurante — el filtro isRanchoSanFrancisco lo muestra sólo a
  // id=28 / slug=rancho-san-francisco.
  ...(isRanchoSanFrancisco ? [{
    name: 'Control de Caja',
    path: `/admin/${slug}/caja`,
    icon: LayoutDashboard,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }] as NavItem[] : []), {
    type: 'group',
    name: 'Estaciones',
    icon: Building2,
    roles: ['owner', 'admin', 'manager', 'caja', 'cocina', 'pizzeria', 'bebidas'] as TenantRole[],
    items: [{
      name: 'KDS (Todas)',
      path: `/admin/${slug}/kds`,
      icon: ChefHat,
      roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
    },
    // Cocina — hidden for Rancho San Francisco (sólo muestra
    // KDS Todas en su grupo de Estaciones).
    ...(isRanchoSanFrancisco ? [] : [{
      name: 'Cocina',
      path: `/admin/${slug}/kds?station=cocina`,
      icon: ChefHat,
      roles: ['owner', 'admin', 'manager', 'caja', 'cocina'] as TenantRole[]
    }]),
    // Pizzería — hidden for La Cabaña Parrillera and Rancho San
    // Francisco (neither restaurant has a pizza station)
    ...(isCabanaParrillera || isRanchoSanFrancisco || isNatuCafe ? [] : [{
      name: 'Pizzería',
      path: `/admin/${slug}/kds?station=pizzeria`,
      icon: Pizza,
      roles: ['owner', 'admin', 'manager', 'caja', 'pizzeria'] as TenantRole[]
    }]),
    // Postres y Batidos — hidden for Rancho San Francisco (sólo
    // KDS Todas en Estaciones).
    ...(isRanchoSanFrancisco ? [] : [{
      // For La Cabaña: rename to "Batidos y postres" to match the
      // owner-facing terminology. Code/route stay the same (station=bebidas).
      name: isCabanaParrillera ? 'Batidos y postres' : 'Postres y Batidos',
      path: `/admin/${slug}/kds?station=bebidas`,
      icon: GlassWater,
      roles: ['owner', 'admin', 'manager', 'caja', 'bebidas'] as TenantRole[]
    }])]
  },
  // Rancho — Dashboard se muestra al nivel superior (mismas funciones
  // que NatuCafé; los datos quedan aislados por restaurant_id vía
  // tenant context, no se cruzan). Control de Caja ya se agregó arriba
  // a nivel superior para Rancho. NatuCafé — Control de Caja se mueve
  // al sub-grupo "911" (Dashboard sigue arriba). Resto: nivel superior.
  // Rancho San Francisco — Dashboard OCULTO del sidebar por completo.
  // El dueño revisa los pedidos abiertos directamente en "Control de
  // Caja" (ya disponible arriba), así que el Dashboard solo distrae.
  // Rancho NO contribuye nada en este slot. Cero impacto en otros
  // restaurantes — sólo este filtro de id=28 / slug=rancho-san-francisco.
  ...(isRanchoSanFrancisco ? [] as NavItem[] : isNatuCafe || isEmilius ? [{
    name: 'Dashboard',
    path: `/admin/${slug}/dashboard`,
    icon: LayoutDashboard,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  } as NavItem] : [{
    name: 'Control de Caja',
    path: `/admin/${slug}/caja`,
    icon: LayoutDashboard,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  } as NavItem, {
    name: 'Dashboard',
    path: `/admin/${slug}/dashboard`,
    icon: LayoutDashboard,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  } as NavItem]), {
    name: 'Menú',
    path: `/admin/${slug}/menu`,
    icon: UtensilsCrossed,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  },
  // Rancho San Francisco — "Mi menú digital QR" justo debajo de Menú.
  // Muestra ÚNICAMENTE el menú digital del restaurante (la misma página
  // MenuQRPage que usan los demás locales, scoped por tenant → no se
  // cruzan datos). Para Rancho, "Otras Herramientas" está oculto, así
  // que lo exponemos acá al nivel superior. Cero impacto en otros locales.
  ...(isRanchoSanFrancisco ? [{
    name: 'Mi menú digital QR',
    path: `/admin/${slug}/menu-qr`,
    icon: QrCode,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  }] as NavItem[] : []), {
    type: 'group',
    name: 'Otras Herramientas',
    icon: Wrench,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[],
    items: [...(isRanchoSanFrancisco ? [{
      name: 'Tomar Orden',
      path: `/admin/${slug}/waiter`,
      icon: ClipboardList,
      roles: ['owner', 'admin', 'manager', 'caja', 'waiter'] as TenantRole[]
    }, {
      name: 'Control de Caja',
      path: `/admin/${slug}/caja`,
      icon: LayoutDashboard,
      roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
    }] as NavItem[] : []), {
      name: 'Enviar Facturas',
      path: `/admin/${slug}/invoice-requests`,
      icon: FileText,
      roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[],
      badge: pendingInvoiceCount
    }, {
      name: 'Métricas',
      path: `/admin/${slug}/metrics`,
      icon: BarChart3,
      roles: ['owner', 'admin', 'manager'] as TenantRole[]
    }, {
      name: 'Inventario',
      path: `/admin/${slug}/inventory`,
      icon: Package,
      roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
    }, {
      name: 'Lealtad',
      path: `/admin/${slug}/loyalty`,
      icon: Award,
      roles: ['owner', 'admin', 'manager'] as TenantRole[]
    }, {
      name: 'Redimir Beneficios',
      path: `/admin/${slug}/loyalty/redemption`,
      icon: Gift,
      roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
    }, {
      name: 'Mi menú digital QR',
      path: `/admin/${slug}/menu-qr`,
      icon: QrCode,
      roles: ['owner', 'admin', 'manager'] as TenantRole[]
    },
    // NatuCafé / Emilius — sub-grupo "911" con accesos rápidos a
    // Tomar Orden y Control de Caja. Va al FINAL de "Otras
    // Herramientas", debajo de "Mi menú digital QR", para que el
    // dueño lo tenga siempre anclado al fondo del sidebar durante
    // el rush.
    ...(isNatuCafe || isEmilius ? [{
      type: 'group',
      name: '911',
      icon: Siren,
      roles: ['owner', 'admin', 'manager', 'caja', 'waiter'] as TenantRole[],
      defaultOpen: true,
      items: [{
        name: 'Tomar Orden',
        path: `/admin/${slug}/waiter`,
        icon: ClipboardList,
        roles: ['owner', 'admin', 'manager', 'caja', 'waiter'] as TenantRole[]
      }, {
        name: 'Control de Caja',
        path: `/admin/${slug}/caja`,
        icon: LayoutDashboard,
        roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
      }]
    } as NavGroup] as NavEntry[] : [])]
  }];
  const isCaprichoAdmin = String(restaurant.id) === '2' || restaurant.slug === 'capricho-de-canela';
  const isCongeladosAdmin = String(restaurant.id) === '18' || restaurant.slug === 'heladeria-congelados';
  const caprichoHidden = ['Tomar Orden', 'Mesas', 'Estaciones'];
  const congeladosHidden = ['Tomar Orden', 'Mesas', 'Estaciones'];
  // super_admin and platform_admin have full access (same as owner)
  const effectiveRole: TenantRole = role === 'super_admin' || role === 'platform_admin' ? 'owner' : role;
  // Filter entries by role + Capricho hides
  // Rancho San Francisco — esconder por completo el grupo
  // "Otras Herramientas" del sidebar. El dueño quiere un sidebar
  // súper limpio (Mesas / Estaciones / Menú). El acceso a Tomar Orden
  // sigue funcionando desde la pantalla de Mesas (tap mesa libre).
  // Rancho — en la vista de KDS (cocina), además ocultamos "Dashboard"
  // del sidebar. El KDS es para la operación de cocina; el Dashboard
  // (resumen/gestión) no debe distraer ahí. Fuera del KDS, Dashboard
  // sigue visible normal. Detección por ruta para no romper otras vistas.
  const isOnKdsView = location.pathname.includes('/kds');
  const ranchoHiddenOnKds = isRanchoSanFrancisco ? isOnKdsView ? ['Otras Herramientas', 'Estaciones', 'Dashboard'] : ['Otras Herramientas', 'Estaciones'] : [];
  // Recursively filter NavEntries by role. Nested NavGroups keep their
  // own items filtered the same way; empty groups get dropped entirely
  // so we never render an empty chevron.
  const filterEntry = (entry: NavEntry): NavEntry | null => {
    if (!entry.roles.includes(effectiveRole)) return null;
    if (isCaprichoAdmin && caprichoHidden.includes(entry.name)) return null;
    if (isCongeladosAdmin && congeladosHidden.includes(entry.name)) return null;
    if (ranchoHiddenOnKds.includes(entry.name)) return null;
    if (isGroup(entry)) {
      const kept = entry.items.map(it => filterEntry(it)).filter((it): it is NavEntry => it !== null);
      if (kept.length === 0) return null;
      return {
        ...entry,
        items: kept
      };
    }
    return entry;
  };
  const filteredEntries: NavEntry[] = allEntries.map(e => filterEntry(e)).filter((e): e is NavEntry => e !== null);
  const handleSignOut = () => {
    if (onSignOut) {
      onSignOut();
    } else {
      navigate(`/admin/${restaurant.slug}/login`);
    }
  };
  const isItemActive = (path: string) => {
    const itemUrl = new URL(path, window.location.origin);
    const currentUrl = new URL(location.pathname + location.search, window.location.origin);
    return path.includes('?') ? itemUrl.pathname === currentUrl.pathname && itemUrl.search === currentUrl.search : location.pathname === path && !location.search;
  };
  const renderNavItem = (item: NavItem, nested = false) => {
    const isActive = isItemActive(item.path);
    return <Link key={item.path} to={item.path} onClick={() => setIsMobileMenuOpen(false)} className={cn('flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200', nested && 'ml-3 py-2.5', isActive ? 'bg-brand-charcoal text-white shadow-md shadow-gray-900/10' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900')} data-id="element-1980">
        <item.icon className={cn('w-5 h-5 shrink-0', isActive ? 'text-white' : 'text-gray-400')} data-id="element-1981" />
        <span className="flex-1 truncate text-sm" data-id="element-1982">{item.name}</span>
        {item.badge !== undefined && item.badge > 0 && <span className={cn('min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold flex items-center justify-center shrink-0', isActive ? 'bg-white text-brand-charcoal' : 'bg-red-500 text-white')} data-id="element-1983">
            {item.badge}
          </span>}
      </Link>;
  };
  // Recursive group renderer. Supports one level of nested groups
  // (currently used by NatuCafé's "911" sub-group inside Otras
  // Herramientas). Nested groups get an extra ml/pl indent so the
  // hierarchy is visually obvious.
  const renderGroup = (group: NavGroup, depth = 0) => {
    const isOpen = openGroups[group.name] ?? group.defaultOpen ?? false;
    const collectBadgeTotal = (entries: NavEntry[]): number => entries.reduce((sum, it) => {
      if (isGroup(it)) return sum + collectBadgeTotal(it.items);
      return sum + (it.badge || 0);
    }, 0);
    const collectHasActiveChild = (entries: NavEntry[]): boolean => entries.some(it => {
      if (isGroup(it)) return collectHasActiveChild(it.items);
      return isItemActive(it.path);
    });
    const hasActiveChild = collectHasActiveChild(group.items);
    const groupBadgeTotal = collectBadgeTotal(group.items);
    const nested = depth > 0;
    return <div key={group.name} className={cn('space-y-1', nested && 'ml-1')} data-id="element-1984">
        <button type="button" onClick={() => setOpenGroups(prev => ({
        ...prev,
        [group.name]: !isOpen
      }))} className={cn('w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200', nested && 'py-2.5', hasActiveChild ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900')} data-id="element-1985">
          <group.icon className={cn('w-5 h-5 shrink-0',
        // "911" gets a red accent to signal it's the emergency /
        // quick-access bucket for the NatuCafé team.
        group.name === '911' ? 'text-red-500' : 'text-gray-400')} data-id="element-1986" />
          <span className={cn('flex-1 text-left truncate text-sm', group.name === '911' && 'font-black tracking-wider text-red-600')} data-id="element-1987">
            {group.name}
          </span>
          {groupBadgeTotal > 0 && !isOpen && <span className="min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold flex items-center justify-center shrink-0 bg-red-500 text-white" data-id="element-1988">
              {groupBadgeTotal}
            </span>}
          <ChevronDown className={cn('w-4 h-4 text-gray-400 transition-transform duration-200 shrink-0', isOpen && 'rotate-180')} data-id="element-1989" />
        </button>
        {isOpen && <div className={cn('space-y-1 pl-2 border-l ml-4', group.name === '911' ? 'border-red-100' : 'border-gray-100')} data-id="element-1990">
            {group.items.map(it => isGroup(it) ? renderGroup(it, depth + 1) : renderNavItem(it, true))}
          </div>}
      </div>;
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row font-sans" data-id="element-1991">
      {/* Mobile Header */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-50 shadow-sm" data-id="element-1992">
        <div className="flex items-center gap-3" data-id="element-1993">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-8 h-8 rounded-lg object-cover border border-gray-100" data-id="element-1994" /> : <div className="w-8 h-8 bg-brand-charcoal rounded-lg flex items-center justify-center" data-id="element-1995">
              <Store className="w-4 h-4 text-white" data-id="element-1996" />
            </div>}
          <span className="font-bold text-gray-900 truncate max-w-[150px]" data-id="element-1997">
            {restaurant.name}
          </span>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 -mr-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1998">
          {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-1999" /> : <Menu className="w-6 h-6" data-id="element-2000" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={cn('fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 flex flex-col shadow-xl md:shadow-none', isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full')} data-id="element-2001">
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-100 flex items-center gap-3" data-id="element-2002">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-10 h-10 rounded-xl object-cover shadow-sm border border-gray-100" data-id="element-2003" /> : <div className="w-10 h-10 bg-brand-charcoal rounded-xl flex items-center justify-center shadow-sm" data-id="element-2004">
              <Store className="w-5 h-5 text-white" data-id="element-2005" />
            </div>}
          <div className="flex-1 min-w-0" data-id="element-2006">
            <h2 className="font-bold text-gray-900 truncate text-lg" data-id="element-2007">
              {restaurant.name}
            </h2>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-2008">
              {role}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1.5" data-id="element-2009">
          {filteredEntries.map(entry => isGroup(entry) ? renderGroup(entry) : renderNavItem(entry))}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-100" data-id="element-2010">
          <button onClick={handleSignOut} className="flex items-center gap-3 px-4 py-3 w-full rounded-xl font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors" data-id="element-2011">
            <LogOut className="w-5 h-5 text-gray-400 group-hover:text-red-500" data-id="element-2012" />
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-30 md:hidden transition-opacity" onClick={() => setIsMobileMenuOpen(false)} data-id="element-2013" />}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-[#FAFAFA]" data-id="element-2014">
        <div className="flex-1 overflow-y-auto" data-id="element-2015">{children}</div>
      </main>
    </div>;
}